import React, { useEffect, useState } from "react";
import SelectAutoCompleteMultipleG from "components/SelectAutoCompleteMultipleG";
import { useListarPDV } from "services/api/AppWeb_Checkin/listar_pdv";
import {
  useFilterCheckin,
  type FilterCheckinContextValue,
  type SelectProps,
} from "context/FilterCheckinContext";
import { format, isValid, parseISO } from "date-fns";
import { RootStateOrAny, useSelector } from "react-redux";
import { AuthState } from "store/ducks/auth/types";
import {
  useListarSuperintendentes,
  useListarEquipes,
  useListarCorretores,
} from "services/api/AppWeb_Checkin/listar_super_equipe_corretor";
import * as IconsSVG from "../../../../assets/iconsSVG";
import * as S from "./styles";
import DatePickerRange from "../DatePickerRange";

interface Props {
  onCloseModal: () => void;
  data: {
    total?: number;
    sorteio?: number;
    posSorteio?: number;
    corretor?: boolean;
    cargoUsuarioRequisicao?: number;
    superioresUsuarioRequisicao?: {
      idUsuarioLiderSuperintendente: string | null;
      nomeSuperintendente: string | null;
      idEquipeSuperintendente: string | null;
      idUsuarioLiderDiretor: string | null;
      nomeDiretor: string | null;
      idEquipeDiretor: string | null;
    } | null;
  } | null;
  isOpen: boolean;
}

const FilterContent: React.FC<Props> = (props) => {
  const {
    filtros,
    atualizarFilterCheckin,
    limparFilterCheckin,
    atualizarCamposDesabilitados,
    disabledFields,
  }: FilterCheckinContextValue = useFilterCheckin();
  const { userId, apelido } = useSelector<RootStateOrAny, AuthState>(
    (state) => state.user
  );

  const [pdvList, setPDVList] = useState<SelectProps[]>([]);
  const [pdv, setPDV] = useState<SelectProps[] | null>([]);
  const [superintendenteList, setSuperintendenteList] = useState<SelectProps[]>(
    []
  );
  const [superintendente, setSuperintendente] = useState<SelectProps[] | null>(
    []
  );
  const [equipe, setEquipe] = useState<SelectProps[] | null>([]);
  const [equipeList, setEquipeList] = useState<SelectProps[]>([]);
  const [corretorList, setCorretorList] = useState<SelectProps[]>([]);
  const [corretor, setCorretor] = useState<SelectProps[] | null>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [superintendenteDisable, setSuperintendenteDisable] =
    useState<boolean>(false);
  const [equipeDisable, setEquipeDisable] = useState<boolean>(false);

  // Hooks com parâmetro enabled controlado pelo estado da modal
  const { data: dataPDV } = useListarPDV({
    queryKey: `listar_pdv`,
    enabled: props.isOpen,
  });

  const { data: dataSuperintendentes } = useListarSuperintendentes({
    queryKey: `listar_superintendentes`,
    userId,
    enabled: props.isOpen,
  });

  const { data: dataEquipes } = useListarEquipes({
    queryKey: `listar_equipes_${superintendente
      ?.map((item) => item.value)
      .join(",")}`,
    IdsSuperintendentes: superintendente?.map((item) => item.value) || [],
    enabled: props.isOpen && !!superintendente && superintendente.length > 0,
  });

  const { data: dataCorretores } = useListarCorretores({
    queryKey: `listar_corretores_${equipe
      ?.map((item) => item.value)
      .join(",")}`,
    IdsEquipes: equipe?.map((item) => item.value) || [],
    enabled: props.isOpen && !!equipe && equipe.length > 0,
  });

  // Efeito para atualizar os estados de desabilitação a partir do contexto
  useEffect(() => {
    if (disabledFields) {
      setSuperintendenteDisable(disabledFields.superintendente);
      setEquipeDisable(disabledFields.equipe);
    }
  }, [disabledFields]);

  useEffect(() => {
    if (
      props?.data?.cargoUsuarioRequisicao &&
      props?.data?.superioresUsuarioRequisicao
    ) {
      if (props.data.cargoUsuarioRequisicao === 1) {
        if (userId && apelido) {
          setSuperintendente([
            {
              value: userId,
              label: apelido,
            },
          ]);
          setSuperintendenteDisable(true);
          atualizarCamposDesabilitados("superintendente", true);
        }
      }

      if (props.data.cargoUsuarioRequisicao === 2) {
        const { idUsuarioLiderSuperintendente, nomeSuperintendente } =
          props.data.superioresUsuarioRequisicao;

        if (idUsuarioLiderSuperintendente && nomeSuperintendente) {
          setSuperintendente([
            {
              value: idUsuarioLiderSuperintendente,
              label: nomeSuperintendente,
            },
          ]);
          setSuperintendenteDisable(true);
          atualizarCamposDesabilitados("superintendente", true);
        }

        if (userId && apelido) {
          setEquipe([
            {
              value: userId,
              label: apelido,
            },
          ]);
          setEquipeDisable(true);
          atualizarCamposDesabilitados("equipe", true);
        }
      }
    }
  }, [props?.data, userId, apelido, atualizarCamposDesabilitados]);

  useEffect(() => {
    if (filtros) {
      setPDV(filtros?.IdsPdvs);
      // Mantenha os valores de superintendente e equipe mesmo quando outros filtros mudam
      if (!superintendenteDisable) {
        setSuperintendente(filtros?.IdsSuperintendentes);
      }
      if (!equipeDisable) {
        setEquipe(filtros?.IdsEquipes);
      }
      setCorretor(filtros?.IdsCorretores);
      setStartDate(
        filtros?.DataInicio ? new Date(parseISO(filtros?.DataInicio)) : null
      );
      setEndDate(
        filtros?.DataFim ? new Date(parseISO(filtros?.DataFim)) : null
      );
    }
  }, [filtros, superintendenteDisable, equipeDisable]);

  useEffect(() => {
    if (dataPDV?.data) {
      setPDVList(
        dataPDV.data.map((item: any) => ({
          value: item.idPdv,
          label: item.nome,
        }))
      );
    }
  }, [dataPDV]);

  useEffect(() => {
    if (dataSuperintendentes?.data) {
      setSuperintendenteList(
        dataSuperintendentes.data.map((item: any) => ({
          value: item.idUsuario,
          label: item.nome,
        }))
      );
    }
  }, [dataSuperintendentes]);

  useEffect(() => {
    if (dataEquipes?.data) {
      setEquipeList(
        dataEquipes.data.map((item: any) => ({
          value: item.idUsuario,
          label: item.nome,
        }))
      );
    }
  }, [dataEquipes]);

  useEffect(() => {
    if (dataCorretores?.data) {
      setCorretorList(
        dataCorretores.data.map((item: any) => ({
          value: item.idUsuario,
          label: item.nome,
        }))
      );
    }
  }, [dataCorretores]);

  // Adicionar valores desabilitados às listas de opções se não existirem
  useEffect(() => {
    // Para superintendente
    if (
      superintendenteDisable &&
      superintendente &&
      superintendente.length > 0
    ) {
      const superintendenteExistsInList = superintendenteList.some(
        (item) => item.value === superintendente[0].value
      );

      if (!superintendenteExistsInList) {
        setSuperintendenteList((prev) => [...prev, ...superintendente]);
      }
    }

    // Para equipe
    if (equipeDisable && equipe && equipe.length > 0) {
      const equipeExistsInList = equipeList.some(
        (item) => item.value === equipe[0].value
      );

      if (!equipeExistsInList) {
        setEquipeList((prev) => [...prev, ...equipe]);
      }
    }
  }, [
    superintendenteDisable,
    superintendente,
    superintendenteList,
    equipeDisable,
    equipe,
    equipeList,
  ]);

  const handleApplyFilter = () => {
    const body = {
      IdsPdvs: pdv,
      IdsSuperintendentes: superintendente,
      IdsEquipes: equipe,
      DataInicio: startDate ? format(startDate, "yyyy-MM-dd") : null,
      DataFim: endDate ? format(endDate, "yyyy-MM-dd") : null,
      IdsCorretores: corretor,
    };
    atualizarFilterCheckin(body);
    props?.onCloseModal();
  };

  // Handler para atualizar a data inicial
  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
  };

  // Handler para atualizar a data final
  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);
  };

  // Função para lidar com a mudança de superintendente, garantindo que os campos permaneçam desabilitados se necessário
  const handleSuperintendenteChange = (event: any, val: any) => {
    if (!superintendenteDisable) {
      setSuperintendente(val);
      if (val.length === 0 && !equipeDisable) {
        setEquipe([]);
        setEquipeList([]);
        setCorretor([]);
        setCorretorList([]);
      }
    }
  };

  // Função para lidar com a mudança de equipe, garantindo que os campos permaneçam desabilitados se necessário
  const handleEquipeChange = (event: any, val: any) => {
    if (!equipeDisable) {
      setEquipe(val);
      if (val.length === 0) {
        setCorretor([]);
        setCorretorList([]);
      }
    }
  };

  return (
    <S.Container>
      <S.HeaderModal>
        <IconsSVG.FilterModalIcon />
        <S.HeaderModalTitle>Filtros</S.HeaderModalTitle>
        <S.ClearBtn onClick={() => limparFilterCheckin()}>
          Limpar campos
        </S.ClearBtn>
      </S.HeaderModal>
      <S.Body>
        <SelectAutoCompleteMultipleG
          data={pdvList}
          label="PDV"
          onChange={(event: any, val: any) => setPDV(val)}
          value={pdv || []}
          isMultiple
          placeholder="Selecione"
        />
        <DatePickerRange
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
        />
        {!props.data?.corretor && props.data?.cargoUsuarioRequisicao !== 3 ? (
          <>
            <SelectAutoCompleteMultipleG
              data={superintendenteList}
              label="Superintendente"
              onChange={handleSuperintendenteChange}
              value={superintendente || []}
              isMultiple
              placeholder="Selecione"
              disabled={superintendenteDisable}
            />
            <SelectAutoCompleteMultipleG
              data={equipeList}
              label="Equipe"
              onChange={handleEquipeChange}
              value={equipe || []}
              isMultiple
              placeholder="Selecione"
              disabled={equipeDisable}
            />
            <SelectAutoCompleteMultipleG
              data={corretorList}
              label="Corretor"
              onChange={(event: any, val: any) => setCorretor(val)}
              value={corretor || []}
              isMultiple
              placeholder="Selecione"
            />
          </>
        ) : null}
      </S.Body>
      <S.FooterModal>
        <S.Button onClick={() => handleApplyFilter()}>Aplicar filtro</S.Button>
      </S.FooterModal>
    </S.Container>
  );
};

export default FilterContent;
